import React, { useEffect, useMemo } from 'react';
import {
  AppButton,
  Box,
  ChipsToggler,
  Controller,
  ControllerWrapper,
  DateInput,
  FieldWrapper,
  FlatTextInput,
  FormProvider,
  Label,
  Option,
  Preloader,
  SelectDropdown,
  useWatch,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { ReportTab, ReportTypeKeys } from '../../report.types';
import { usePlanOmnibusBalanceVisible } from '../../ReportDetails/PlanOmnibusBalanceReport/hooks';
import {
  REPORT_TYPE_OPTIONS,
  ScheduledFormPayload,
  ScheduledReportPayload,
  SETUP_SCHEDULER_FOOTER_PORTAL_ID,
} from '../ScheduledReport.types';

import CronBuilderField from './CronBuilderField';
import { useScheduledReportForm } from './hooks';
interface Props extends Partial<ScheduledReportPayload> {
  onNext: (value: ScheduledReportPayload) => void;
  onChangeType?: (type: ReportTypeKeys) => void;
  onChangeTab?: (tab: ReportTab) => void;
  onClose: () => void;
  isLoading?: boolean;
  isEditMode?: boolean;
  defaultValues?: Partial<ScheduledFormPayload>;
}
const DISABLED_SUMMARY_REPORTS = [ReportTypeKeys.EmployeeDemographic, ReportTypeKeys.UndeliverableAddress];

const ScheduledReportForm: React.FC<Props> = ({
  type,
  onNext,
  onClose,
  onChangeType,
  isLoading = false,
  defaultValues,
  onChangeTab,
  isEditMode,
  ...params
}) => {
  const methods = useScheduledReportForm({
    defaultValues: {
      type,
      tab: params.tab,
      ...defaultValues,
    },
  });
  const reportType = useWatch({
    control: methods.control,
    name: 'type',
  });

  const isSummaryDisabled = useMemo(() => {
    return DISABLED_SUMMARY_REPORTS.includes(reportType);
  }, [reportType]);
  useEffect(() => {
    if (isSummaryDisabled) {
      methods.setValue('tab', ReportTab.Report);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSummaryDisabled]);
  const errors = useMemo(() => methods.formState.errors, [methods.formState.errors]);
  const handleSubmit = (newValue: ScheduledFormPayload) => {
    onNext(newValue as ScheduledReportPayload);
  };
  const yesterday = useMemo(() => dayjs().add(-1, 'day'), []);

  const chipsOptions = useMemo<Option[]>(() => {
    if (isSummaryDisabled) {
      return [
        {
          key: ReportTab.Report,
          title: 'Report',
          disabled: true,
        },
      ];
    }
    if (isEditMode) {
      return [
        {
          key: defaultValues?.tab === ReportTab.Summary ? ReportTab.Summary : ReportTab.Report,
          title: defaultValues?.tab === ReportTab.Summary ? 'Summary' : 'Report',
          disabled: true,
        },
      ];
    }
    return [
      {
        key: ReportTab.Summary,
        title: 'Summary',
      },
      {
        key: ReportTab.Report,
        title: 'Report',
      },
    ];
  }, [defaultValues?.tab, isEditMode, isSummaryDisabled]);
  const isOmnibusBalanceVisible = usePlanOmnibusBalanceVisible();
  const reportTypesOptions = useMemo(() => {
    let list = REPORT_TYPE_OPTIONS;
    if (!isOmnibusBalanceVisible) {
      list = list.filter((option) => option.key !== ReportTypeKeys.PlanOmnibusBalance);
    }

    return list;
  }, [isOmnibusBalanceVisible]);
  return (
    <FormProvider {...methods}>
      <Box direction="column">
        <Box margin={{ top: 'spacing16' }} background="module" round="moduleRound" pad="spacing24">
          <Box background="canvas" round="container1Round" pad="spacing24">
            <FieldWrapper align="start">
              <Label name="name" margin={{ top: 'spacing6' }} showRequireIcon isNarrow={true}>
                Schedule name
              </Label>
              <Controller
                name="name"
                control={methods.control}
                render={({ field }) => (
                  <FlatTextInput
                    testId="scheduled-report_name"
                    placeholder="Schedule name"
                    value={field.value}
                    errorText={errors?.name?.message}
                    onChange={field.onChange}
                  />
                )}
              />
            </FieldWrapper>
            <FieldWrapper align="start" margin={{ top: 'spacing16' }}>
              <Label showRequireIcon={!type} name="type" margin={{ top: 'spacing6' }} isNarrow={true}>
                Report
              </Label>
              <Controller
                name="type"
                control={methods.control}
                render={({ field }) => (
                  <SelectDropdown
                    options={reportTypesOptions}
                    disabled={!!type || isEditMode}
                    testId="scheduled-report_report-type"
                    errorText={errors?.type?.message}
                    value={field.value}
                    onChange={(newProps) => {
                      if (onChangeType) {
                        onChangeType(newProps as ReportTypeKeys);
                      }
                      field.onChange(newProps);
                    }}
                    placeholder="Select report type"
                    singleMode
                    id="type"
                    name="type"
                  />
                )}
              />
            </FieldWrapper>

            <FieldWrapper align="start" margin={{ top: 'spacing16' }}>
              <Label name="tab" margin={{ top: 'spacing6' }} isNarrow={true}>
                Schedule for
              </Label>

              <Controller
                name="tab"
                control={methods.control}
                render={({ field }) => (
                  <ChipsToggler
                    options={chipsOptions}
                    value={field.value}
                    buttonStyle={{
                      minHeight: '40px',
                    }}
                    onChange={(value) => {
                      if (onChangeTab) {
                        onChangeTab(value as ReportTab);
                      }
                      field.onChange(value);
                    }}
                  />
                )}
              />
            </FieldWrapper>
            <Controller
              name="cronString"
              control={methods.control}
              render={({ field }) => (
                <CronBuilderField
                  value={field.value}
                  onSetValue={field.onChange}
                  errorMessage={errors?.cronString?.message}
                >
                  <FieldWrapper align="start" margin={{ top: 'spacing16' }}>
                    <Label showRequireIcon name="startDate" margin={{ top: 'spacing6' }} isNarrow={true}>
                      Start date
                    </Label>
                    <ControllerWrapper errorText={errors?.startDate?.message}>
                      <Controller
                        name="startDate"
                        control={methods.control}
                        render={({ field }) => (
                          <DateInput
                            style={{ maxWidth: '160px' }}
                            isOnlySelector
                            minDate={yesterday}
                            isError={!!errors?.startDate?.message}
                            testId="scheduled-report_start-date"
                            placeholder="Select date"
                            value={field.value || ''}
                            onChange={(value) => {
                              field.onChange(value);
                              methods.trigger('endDate');
                            }}
                          />
                        )}
                      />
                    </ControllerWrapper>
                  </FieldWrapper>
                  <FieldWrapper align="start" margin={{ top: 'spacing16' }}>
                    <Label name="endDate" margin={{ top: 'spacing6' }} isNarrow={true}>
                      End date
                    </Label>
                    <ControllerWrapper errorText={errors?.endDate?.message}>
                      <Controller
                        name="endDate"
                        control={methods.control}
                        render={({ field }) => (
                          <DateInput
                            style={{ maxWidth: '160px' }}
                            isOnlySelector
                            minDate={yesterday}
                            testId="scheduled-report_end-date"
                            isError={!!errors?.endDate?.message}
                            placeholder="Select date"
                            value={field.value || ''}
                            onChange={(value) => {
                              field.onChange(value);
                              methods.trigger('endDate');
                            }}
                          />
                        )}
                      />
                    </ControllerWrapper>
                  </FieldWrapper>
                </CronBuilderField>
              )}
            />
          </Box>
          <Box id={SETUP_SCHEDULER_FOOTER_PORTAL_ID} />
        </Box>
        <Box direction="row" justify="end" margin={{ top: 'spacing24' }}>
          <AppButton testId="scheduled-report_cancel" buttonType="secondary" width="160px" onClick={onClose}>
            Cancel
          </AppButton>
          <Box margin={{ left: 'spacing12' }}>
            <AppButton
              testId="scheduled-report_next"
              onClick={methods.handleSubmit(handleSubmit)}
              disabled={isLoading}
              width="160px"
            >
              {isLoading ? <Preloader testId="scheduled-report_preloader" /> : 'Next (1/2)'}
            </AppButton>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};
export default ScheduledReportForm;
